import { useEffect, useState } from "react";
import {
  Stack,
  Box,
  Divider,
  Checkbox,
  Typography,
  Chip,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CustomDialog from "../../Components/CustomDialog";
import CustomTextField from "../../Components/CustomTextfield";
import { subHeading } from "../../Components/CustomTexts";
import { contents } from "../../store/slices/constants/staticContents";
import { ObjectValues } from "../login";
import { justifEnd } from "../../GlobalStyles";
import { DeletionItem } from "../../store/slices/pendingApproval";

type BulkDeleteProps = {
  dialogs: any;
  setDialog: (val: any) => void;
  row?: DeletionItem | null;
};

const remarkCard = (
  <Box mt={1}>
    <Typography fontWeight={600} mb={1}>
      1. Deleting all Questions will also delete:
    </Typography>
    <Stack direction="row" flexWrap="wrap" mb={2}>
      {[
        "Options",
        "Translations",
        "Explanations",
        "Responses",
        "Saved by user",
        "Reported by user",
        "Question-Mock Tests Mapping",
      ].map((item) => (
        <Chip key={item} label={item} sx={{ m: "4px" }} variant="outlined" />
      ))}
    </Stack>
    <Typography fontWeight={600} mb={1}>
      2. User's Progress Related Metrics
    </Typography>
    <Chip label="User's Progress" variant="outlined" sx={{ m: "4px" }} />
  </Box>
);

export const deleteOptions = [
  { id: 1, label: "A - Delete All Subtopics", api: "A" },
  {
    id: 2,
    label: "B - Delete All Subtopic-Cluster Mapping in this topic",
    api: "B",
  },
  { id: 3, label: "C - Delete All Clusters", api: "C" },
  { id: 4, label: "D - Delete All Questions", remark: remarkCard, api: "D" },
  { id: 5, label: "E - Delete entire Topic", api: "E" },
];

const initialDetails: ObjectValues = {
  1: { disabled: false, checked: false },
  2: { disabled: false, checked: false },
  3: { disabled: false, checked: false },
  4: { disabled: false, checked: false },
  5: { disabled: false, checked: false },
};

const affectedIdsMap: Record<number, number[]> = {
  1: [2],
  2: [3, 4],
  3: [2, 4],
  4: [],
  5: [1, 2, 3, 4],
};

const BulkDelete = ({ dialogs, setDialog, row }: BulkDeleteProps) => {
  const [details, setDetails] = useState(initialDetails);
  const [activeStep, setActiveStep] = useState(0);
  const [stepOneEditable, setStepOneEditable] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkBoxClick = (id: number) => {
    setDetails((prev) => {
      const isChecked = prev[id].checked;
      const updates: Partial<ObjectValues> = {
        [id]: { disabled: false, checked: !isChecked },
      };

      affectedIdsMap[id].forEach((affectedId) => {
        updates[affectedId] = {
          disabled: !isChecked,
          checked: !isChecked,
        };
      });

      const simulatedState = { ...prev, ...updates };
      const requiredIds = [1, 2, 3, 4];
      const allChecked = requiredIds.every(
        (checkId) => simulatedState[checkId]?.checked
      );

      // if (allChecked && !prev[5].checked && !updates[5]) {
      //   updates[5] = { disabled: true, checked: true };
      // } else
      if (!allChecked && prev[5].checked) {
        updates[5] = { disabled: false, checked: false };
      }

      return { ...prev, ...updates };
    });
  };

  const isSaveDisabled = () =>
    !Object.values(details).some((item) => item.checked && !item.disabled);

  const steps = [
    {
      label: `Checklist Before Deleting the topic ${dialogs.selected?.name} `,
      content: (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            {deleteOptions.map(({ id, label, remark }) => (
              <Box key={id}>
                <Box display="flex" alignItems="center">
                  <Checkbox
                    disabled={dialogs.disabled?dialogs.disabled:loading ? loading : details[id].disabled}
                    checked={details[id].checked}
                    onChange={() => checkBoxClick(id)}
                  />
                  <Typography>{label}</Typography>
                </Box>
                {details[id].checked && remark && <Box ml="42px">{remark}</Box>}
              </Box>
            ))}
          </Stack>
        </Box>
      ),
    },
    {
      label: "Feedback",
      content: (
        <Box>
          <Box sx={{ color: "#A09F9F" }} mb={1}>
            {subHeading({
              text: contents.actionText(
                dialogs.selected?.name,
                `${dialogs.title} of topic`
              ),
            })}
          </Box>
          <Divider sx={{ mb: 2 }} />
          {subHeading({ text: contents.feedback, weight: 600 })}
          <CustomTextField
            placeholder={contents.feedback}
            id="feedback-content"
            variant="outlined"
            value={dialogs[contents.feedback] ?? ""}
            maxRows={6}
            minRows={6}
            disabled={loading ? loading : dialogs.disabled ?? false}
            multiline
            onChange={(e) =>
              setDialog((p: any) => ({
                ...p,
                [contents.feedback]: e.target.value,
              }))
            }
            sx={{
              width: "100%",
              "& .MuiInputBase-root": { padding: 0 },
              "& .MuiInputBase-input": { padding: "10px 12px" },
            }}
            inputProps={{
              "data-testid": `${contents.feedback}-input`,
            }}
          />
        </Box>
      ),
    },
  ];

  const scrollToStep = () => {
    const ref = document.getElementById("feedback-content");
    ref?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  useEffect(() => {
    const setSelectedOptions = (selectedApis: string[]) => {
      setDetails((prevDetails) => {
        const updatedDetails = { ...prevDetails };

        deleteOptions.forEach((opt) => {
          updatedDetails[opt.id] = {
            ...updatedDetails[opt.id],
            checked: selectedApis.includes(opt.api),
          };
        });

        return updatedDetails;
      });
    };
    if (row && row.options) {
      setSelectedOptions(row.options);
      if (dialogs.disabled) {
        setStepOneEditable(false);
        setActiveStep(1);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);
  const onClose = () => {
    setActiveStep(0);
    setDetails(initialDetails);
    setStepOneEditable(false);
    setDialog({});
  };
  return (
    <CustomDialog
      title={
        dialogs.type === "approve"
          ? `Approve Deletion of Items in Bulk`
          : dialogs.type === "reject"
          ? `Reject Deletion of Items in Bulk`
          : dialogs.type === "update"
          ? `Update Deletion of Items in Bulk`
          : `Delete Items in Bulk ${
              dialogs.selected?.request_date
                ? `Created (${dialogs.selected?.request_date})`
                : ""
            }`
      }
      open={dialogs.open}
      onClose={() => {
        onClose();
      }}
      contentsx={{
        minHeight: "431px !important",
      }}
      actionsx={justifEnd}
      actions={
        dialogs.disabled
          ? dialogs.type === "approve" || dialogs.type === "reject"
            ? [
                {
                  sx: { width: "130px", padding: "6px", minHeight: "37.19px" },
                  content: "Cancel",
                  variant: "outlined",
                  onClick: () => {
                    onClose();
                  },
                  disabled: loading,
                  // loading: loading,
                },
                {
                  sx: { width: "130px", padding: "6px", minHeight: "37.19px" },
                  content: dialogs.type === "approve" ? "Approve" : "Reject",
                  variant: "contained",
                  disabled:
                    activeStep === 0
                      ? isSaveDisabled()
                      : !(dialogs[contents.feedback]?.length > 0),
                  onClick: () => {
                    setLoading(true);
                    const getSelectedOptions = () => {
                      return deleteOptions
                        .filter((opt) => details[opt.id].checked)
                        .map((opt) => opt.api);
                    };

                    const selectedOptions = getSelectedOptions();

                    dialogs
                      .actions?.(dialogs, selectedOptions)?.[0]
                      ?.onClick?.()
                      .then(() => {
                        setLoading(false);
                      });
                  },
                  loading: loading,
                },
              ]
            : []
          : [
              {
                sx: { width: "130px", padding: "6px", minHeight: "37.19px" },
                content: activeStep === 0 ? "Continue" : "Submit",
                variant: "contained",
                disabled:
                  activeStep === 0
                    ? isSaveDisabled()
                    : !(dialogs[contents.feedback]?.length > 0),
                onClick: () => {
                  if (activeStep === 0) {
                    setStepOneEditable(false);
                    setActiveStep(1);
                    setTimeout(() => {
                      scrollToStep();
                    }, 300);
                  } else {
                    setLoading(true);
                    const getSelectedOptions = () => {
                      return deleteOptions
                        .filter((opt) => details[opt.id].checked)
                        .map((opt) => opt.api);
                    };

                    const selectedOptions = getSelectedOptions();

                    dialogs
                      .actions?.(dialogs, selectedOptions)?.[0]
                      ?.onClick?.()
                      .then(() => {
                        // setLoading(false);
                      });
                  }
                },
                loading: loading,
              },
            ]
      }
      content={
        <Box width="540px">
          <Stepper
            activeStep={activeStep}
            orientation="vertical"
            sx={{
              pl: 1,
              "& .MuiStepConnector-line": {
                borderLeftStyle: "dashed",
                visibility: activeStep === 0 ? "hidden" : "inherit",
              },
            }}
          >
            {steps.map((step, index) => (
              <Step
                key={step.label}
                sx={{
                  visibility:
                    activeStep === 0 && index === 1 ? "hidden" : "inherit",
                  height:
                    activeStep === 0 && index === 1 ? "1px" : "fit-content",
                  overflow:
                    activeStep === 0 && index === 1 ? "hidden" : "inherit",
                }}
              >
                <StepLabel
                  sx={{
                    cursor:
                      index === 0 && activeStep === 1 && !stepOneEditable
                        ? "not-allowed"
                        : "pointer",
                    color:
                      index === 0 && activeStep === 1 && !stepOneEditable
                        ? "gray"
                        : "inherit",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    "& .MuiStepLabel-label": {
                      fontWeight: 600,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      opacity:
                        index === 0 && activeStep === 1 && !stepOneEditable
                          ? 0.5
                          : 1,
                    },
                  }}
                >
                  <Typography width="fit-content">{step.label}</Typography>
                  {dialogs.disabled
                    ? ""
                    : index === 0 &&
                      activeStep === 1 && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStepOneEditable(true);
                            setActiveStep(0);
                          }}
                          sx={{ ml: 1 }}
                          disabled={loading}
                        >
                          <EditIcon fontSize="small" sx={{ color: "black" }} />
                        </IconButton>
                      )}
                </StepLabel>
                <Box
                  sx={{
                    pl: 3,
                    pt: 1,
                    pb: 3,
                    pointerEvents:
                      index === 0 && activeStep === 1 && !stepOneEditable
                        ? "none"
                        : index === 1 && activeStep === 0
                        ? "none"
                        : "inherit",
                  }}
                >
                  {step.content}
                </Box>
              </Step>
            ))}
          </Stepper>
        </Box>
      }
    />
  );
};

export default BulkDelete;
